import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseUrl, formatDate } from "../../comman/config";
import { IngredientFilter } from '../../components/Filter/IngredientFilter';
import Loader from '../../components/Loader';
import { isFilterActive, matchFilterText, patchFilter } from "../../components/Filter/columnFilter";
import ColumnFilterRow from "../../components/Filter/ColumnFilterRow";
import { ItemName } from "../../components/ItemName";
import toast, { Toaster } from "react-hot-toast";
import NoRecordsFound from "../../components/NoRecordsFound";
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import gujaratiFont from '../../resources/NotoSansGujarati';
import * as XLSX from 'xlsx';

function IngredientRequirementEstimation() {
    const [ingredientData, setIngredientData] = useState([]);
    // Inline per-column filters: { [colKey]: { op, v1, v2 } }. FILTER_COLS lists the body columns
    // in table order and returns each cell's plain text -- several render a link, a coloured span
    // or an input, and a filter has to see the value inside, not the markup. It is the same list
    // the header and the rows are built from, so the three superadmin-only columns drop out of all
    // three together and the filter row stays lined up with the table.
    // The Select checkbox column has no filter, but keeps a cell so the row stays aligned.
    const [filters, setFilters] = useState({});
    const setColFilter = (key, patch) => setFilters((prev) => patchFilter(prev, key, patch));
    const qty = (v) => (v > 0 ? v.toFixed(2) : '-');
    // Closing Qty (System), Available Qty. and Net Required Qty are the stock working behind the
    // estimate, and only the superadmin is shown them -- everyone else sees the gross requirement
    // and the Indent Qty. they are there to set. Role 0 is the superadmin, role 1 the ordinary
    // admin. Hiding a column is presentation: the figures still come down in the response and the
    // Indent Qty. default is still worked out from them.
    const isSuperAdmin = Number(localStorage.getItem("userRole")) === 0;
    const SUPERADMIN_ONLY_COLS = ['closing', 'actual', 'net_event'];
    const FILTER_COLS = [
        { key: 'select',      filterable: false },
        { key: 'store',       text: (r) => r.store_name },
        { key: 'category',    text: (r) => r.ingredient_category_name },
        { key: 'ingredient',  text: (r) => `${r.ingredient_name} ${r.eng_name ? `(${r.eng_name})` : ''}`.trim() },
        { key: 'unit',        text: (r) => r.unit },
        { key: 'gross_event', text: (r) => qty(r.event_wise_qty) },
        { key: 'closing',     text: () => 0 },
        { key: 'actual',      text: (r) => r.actual_qty },
        { key: 'net_event',   text: (r) => qty(r.net_required_event) },
        { key: 'order_qty',   text: (r) => r.order_qty ?? 0 },
        { key: 'order_pack',  text: (r) => r.order_pack_nos || 0 },
    ].filter((c) => isSuperAdmin || !SUPERADMIN_ONLY_COLS.includes(c.key));
    const activeFilterCols = FILTER_COLS.filter((c) => c.filterable !== false && isFilterActive(filters[c.key]));
    // Keeps the ORIGINAL ingredientData index: the checkbox and the two order inputs identify
    // their row by position, so filtering must not renumber them.
    const visibleIngredients = ingredientData
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => activeFilterCols.every((c) => matchFilterText(c.text(item), filters[c.key])));
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [session, setSession] = useState([]);
    const [item, setItem] = useState([]);
    const [event, setEvent] = useState({});
    const [ingredients, setIngredients] = useState([]);
    const [store, setStore] = useState([]);
    const [category, setCategory] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [noRecordsFound, setNoRecordsFound] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [ingredientDetails, setIngredientDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [modalType, setModalType] = useState(''); // 'day', 'event', or 'combined'

    useEffect(() => {
        if (fromDate && toDate) {
            getIngredientsData();
        }
    }, [fromDate, toDate, session, item, event, ingredients, store, category]);

    const getIngredientsData = async () => {
        setLoading(true);
        try {
            // Get ingredients requirement data
            const response = await axios.get(`${baseUrl}/reports/ingredients_total_report`, {
                params: {
                    fromDate,
                    toDate,
                    session: JSON.stringify(session),
                    item: JSON.stringify(item),
                    event: JSON.stringify(event),
                    ingredients: JSON.stringify(ingredients),
                    store: JSON.stringify(store),
                    category: JSON.stringify(category),
                }
            });

            if (response.data.data.length === 0) {
                setNoRecordsFound(true);
                setIngredientData([]);
            } else {
                setNoRecordsFound(false);

                const ingredientIds = response.data.data.map(item => item.ingredient_id);

                // Fetch existing order data for these ingredients
                const orderResponse = await axios.get(`${baseUrl}/stock_ingredient/list`, {
                    params: {
                        ingredientIds: JSON.stringify(ingredientIds)
                    }
                });

                const existingOrders = orderResponse.data.data || [];

                // Get actual stock data for each ingredient
                const enrichedData = await Promise.all(response.data.data.map(async (item) => {
                    try {
                        const stockResponse = await axios.get(`${baseUrl}/stock_report/list?ingredients=${item.ingredient_id}`)
                        const stockItem = stockResponse.data.data.find(stock => stock.ingredient_id === item.ingredient_id);
                        const actualStock = stockItem ? parseFloat(stockItem.quantity) : 0;

                        // Calculate required quantities
                        const dayWiseRequired = item.day_wise_qty || 0;
                        const eventWiseRequired = item.event_wise_qty || 0;

                        // Calculate net required for day and event separately
                        const netRequiredDay = Math.max(dayWiseRequired - actualStock, 0);
                        const netRequiredEvent = Math.max(eventWiseRequired - actualStock, 0);

                        // const totalRequired = dayWiseRequired + eventWiseRequired;
                        // const netRequired = netRequiredDay + netRequiredEvent;

                        const existingOrder = existingOrders.find(order => order.ingredient_id === item.ingredient_id);

                        // Determine which order quantity to use based on the specified logic
                        let orderQty = 0;
                        if (existingOrder) {
                            orderQty = parseFloat(existingOrder.order_qty);
                        } else if (netRequiredDay > 0 && netRequiredEvent > 0) {
                            // If both day and event have net required, take day
                            orderQty = netRequiredDay;
                        } else if (netRequiredDay > 0) {
                            // If only day has net required
                            orderQty = netRequiredDay;
                        } else if (netRequiredEvent > 0) {
                            // If only event has net required
                            orderQty = netRequiredEvent;
                        }

                        return {
                            ...item,
                            actual_qty: actualStock,
                            day_wise_qty: dayWiseRequired,
                            event_wise_qty: eventWiseRequired,
                            // total_planned_qty: totalRequired,
                            net_required_day: netRequiredDay,
                            net_required_event: netRequiredEvent,
                            // net_required_qty: netRequired,
                            order_qty: orderQty,
                            order_pack_nos: existingOrder ? parseFloat(existingOrder.order_pack_nos) : 0,
                            selected: false,
                            has_day_wise: dayWiseRequired > 0,
                            has_event_wise: eventWiseRequired > 0
                        };
                    } catch (error) {
                        console.error("Error fetching stock for ingredient:", error);
                        return {
                            ...item,
                            actual_qty: 0,
                            day_wise_qty: item.day_wise_qty || 0,
                            event_wise_qty: item.event_wise_qty || 0,
                            total_planned_qty: item.total_planned_qty || 0,
                            net_required_day: Math.max((item.day_wise_qty || 0) - 0, 0),
                            net_required_event: Math.max((item.event_wise_qty || 0) - 0, 0),
                            // net_required_qty: item.total_planned_qty || 0,
                            order_qty: item.day_wise_qty > 0 ? Math.max((item.day_wise_qty || 0) - 0, 0) :
                                Math.max((item.event_wise_qty || 0) - 0, 0),
                            order_pack_nos: 0,
                            selected: false,
                            has_day_wise: (item.day_wise_qty || 0) > 0,
                            has_event_wise: (item.event_wise_qty || 0) > 0
                        };
                    }
                }));

                setIngredientData(enrichedData);
            }
        } catch (error) {
            console.error("Error fetching ingredient data:", error);
            toast.error("Failed to fetch ingredient data. Please try again.");
            setNoRecordsFound(true);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!ingredientData || ingredientData.length === 0) {
            console.log('No data to export');
            return;
        }

        // Generate title
        const eventName = event && event.label ? event.label : "Event";
        const formattedTitle = `${eventName} General Kitchen Menu \nShibir Dates : ${fromDate ? formatDate(fromDate) : ''} - ${toDate ? formatDate(toDate) : ''}`;

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([]);

        // Add title at the top
        XLSX.utils.sheet_add_aoa(worksheet, [[formattedTitle]], { origin: "A1" });

        // The indent sheet the kitchen sends out is not the screen: six columns, the last of them
        // the indent figure. Closing, Available, Net Required and Pack Nos. stay on the screen and
        // off the sheet.
        const headers = ["Store", "Ingredient Category", "Ingredient Name", "Unit", "Gross Required Qty.", "Final Indent Qty"];
        XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A3" });

        // Populate data
        let rowIndex = 4;

        ingredientData.forEach(item => {
            // Final Indent Qty is the Indent Qty. as it stands on the screen, edits included --
            // ingredientData holds them, so an unsaved change still reaches the sheet. Nothing to
            // order leaves the cell empty, as it always was, for the store to fill in by hand.
            const indentQty = Number(item.order_qty);
            const rowData = [
                item.store_name,
                item.ingredient_category_name,
                item.eng_name ? `${item.ingredient_name} (${item.eng_name})` : item.ingredient_name,
                item.unit,
                item.event_wise_qty.toFixed(2),
                indentQty > 0 ? indentQty.toFixed(2) : ""
            ];

            XLSX.utils.sheet_add_aoa(worksheet, [rowData], { origin: `A${rowIndex}` });
            rowIndex++;
        });

        // Set column widths
        worksheet['!cols'] = [
            { wch: 20 }, // Store
            { wch: 25 }, // Ingredient Category
            { wch: 30 }, // Ingredient Name
            { wch: 10 }, // Unit
            { wch: 18 }, // Gross Required Qty.
            { wch: 16 }, // Final Indent Qty
        ];

        // Merge title row across columns
        worksheet["!merges"] = [{
            s: { r: 0, c: 0 },
            e: { r: 0, c: headers.length - 1 }
        }];

        // Set row heights
        worksheet['!rows'] = [
            { hpt: 30 }, // Title
            , // Empty row
            { hpt: 20 }, // Header
            ...Array(rowIndex - 3).fill({ hpt: 25 }) // Data rows
        ];

        // Enable text wrapping for all data cells
        for (let row = 4; row < rowIndex; row++) {
            for (let col = 0; col < headers.length; col++) {
                const cellRef = XLSX.utils.encode_cell({ r: row - 1, c: col });
                if (worksheet[cellRef]) {
                    worksheet[cellRef].s = { alignment: { wrapText: true, vertical: 'top' } };
                }
            }
        }

        // Generate and download file
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Ingredients Report');
        XLSX.writeFile(workbook, `${eventName}_Ingredients_Report.xlsx`);
    };

    const handleExportPdf = () => {
        if (!ingredientData || ingredientData.length === 0) {
            console.log('No data to export');
            return;
        }

        const eventName = event && event.label ? event.label : "Event";
        const eventTitle = `${eventName} General Kitchen Menu  `;
        const eventSubTitle = `Shibir Dates : ${fromDate ? formatDate(fromDate) : ''} - ${toDate ? formatDate(toDate) : ''}`;

        const doc = new jsPDF();
        doc.addFont(gujaratiFont, 'Gujarati', 'normal');
        doc.setFont('Gujarati');

        // Add title to PDF
        doc.setFontSize(16);
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.text(eventTitle, pageWidth / 2, 15, { align: "center" });
        doc.text(eventSubTitle, pageWidth / 2, 25, { align: "center" });

        // Prepare data for table. One list drives the headings, the cells and the widths, so the
        // three superadmin-only columns can drop out without the widths landing on the wrong
        // columns -- autoTable keys columnStyles by position.
        const pdfCols = [
            { header: "Store",       width: 18, value: (row) => row.store_name },
            { header: "Category",    width: 20, value: (row) => row.ingredient_category_name },
            { header: "Ingredient",  width: 25, value: (row) => row.eng_name ? `${row.ingredient_name} (${row.eng_name})` : row.ingredient_name },
            { header: "Unit",        width: 8,  value: (row) => row.unit },
            { header: "Gross Qty",   width: 14, value: (row) => row.event_wise_qty.toFixed(2) },
            { header: "Closing",     width: 12, value: () => 0, superadminOnly: true },
            { header: "Available",   width: 14, value: (row) => row.actual_qty, superadminOnly: true },
            { header: "Net Qty",     width: 14, value: (row) => row.net_required_event.toFixed(2), superadminOnly: true },
            { header: "Indent Qty.", width: 12, value: (row) => row.order_qty },
            { header: "Pack Nos",    width: 10, value: (row) => row.order_pack_nos },
        ].filter((c) => isSuperAdmin || !c.superadminOnly);

        const tableColumn = pdfCols.map((c) => c.header);
        const tableRows = ingredientData.map((row) => pdfCols.map((c) => c.value(row)));

        // Add table to PDF
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 30,
            theme: 'grid',
            styles: {
                fontSize: 8,
                font: 'Gujarati'
            },
            columnStyles: Object.fromEntries(pdfCols.map((c, i) => [i, { cellWidth: c.width }])),
            headStyles: { fillColor: [66, 139, 202] }
        });

        // Save PDF
        doc.save(`${eventName}_Ingredients_Report.pdf`);
    };

    const handleFilterChange = (fromDate, toDate, session, item, event, ingredients, store, category) => {
        setFromDate(fromDate);
        setToDate(toDate);
        setSession(session);
        setItem(item);
        setEvent(event);
        setIngredients(ingredients);
        setStore(store);
        setCategory(category);
    };

    const handleToggleSelect = (index) => {
        const updatedData = [...ingredientData];
        updatedData[index].selected = !updatedData[index].selected;
        setIngredientData(updatedData);

        // Update selectedRows
        if (updatedData[index].selected) {
            setSelectedRows([...selectedRows, updatedData[index]]);
        } else {
            setSelectedRows(selectedRows.filter(item => item.ingredient_id !== updatedData[index].ingredient_id));
        }
    };

    const handleSwitchSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        const updatedData = ingredientData.map(item => ({
            ...item,
            selected: newSelectAll
        }));

        setIngredientData(updatedData);
        setSelectedRows(newSelectAll ? [...updatedData] : []);
    };

    const handleOrderQtyChange = (index, value) => {
        const updatedData = [...ingredientData];
        updatedData[index].order_qty = parseFloat(value) || 0;
        setIngredientData(updatedData);

        // Update selected rows if this item is selected
        if (updatedData[index].selected) {
            setSelectedRows(prevSelectedRows => {
                return prevSelectedRows.map(row =>
                    row.ingredient_id === updatedData[index].ingredient_id ? updatedData[index] : row
                );
            });
        }
    };

    const handleOrderQtyBlur = (index) => {
        const updatedData = [...ingredientData];
        let qty = updatedData[index].order_qty;

        if (qty !== null && qty !== undefined && !isNaN(qty)) {
            qty = parseFloat(qty).toFixed(2);
            updatedData[index].order_qty = parseFloat(qty); // Save as number
        } else {
            updatedData[index].order_qty = 0;
        }

        setIngredientData(updatedData);

        // Update selected rows if this item is selected
        if (updatedData[index].selected) {
            setSelectedRows((prevSelectedRows) => {
                return prevSelectedRows.map((row) =>
                    row.ingredient_id === updatedData[index].ingredient_id ? updatedData[index] : row
                );
            });
        }
    };

    const handleOrderPackNosChange = (index, value) => {
        const updatedData = [...ingredientData];
        updatedData[index].order_pack_nos = parseFloat(value) || 0;
        setIngredientData(updatedData);

        // Update selected rows if this item is selected
        if (updatedData[index].selected) {
            setSelectedRows(prevSelectedRows => {
                return prevSelectedRows.map(row =>
                    row.ingredient_id === updatedData[index].ingredient_id ? updatedData[index] : row
                );
            });
        }
    };

    const getIngredientDetails = async (ingredientId, type) => {
        setLoadingDetails(true);
        try {
            const response = await axios.get(`${baseUrl}/reports/ingredient_usage_details`, {
                params: {
                    ingredientId,
                    fromDate,
                    toDate,
                    session: JSON.stringify(session),
                    item: JSON.stringify(item),
                    event: JSON.stringify(event)
                }
            });

            // Sort details by type (event vs day)
            let sortedDetails = response.data.data;

            if (type === 'day') {
                sortedDetails = sortedDetails.filter(detail => detail.source_type === 'day');
            } else if (type === 'event') {
                sortedDetails = sortedDetails.filter(detail => detail.source_type === 'event');
            }

            setIngredientDetails(sortedDetails);
        } catch (error) {
            console.error("Error fetching ingredient details:", error);
            setIngredientDetails([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Handle click on Day-wise or Event-wise Qty cell
    const handleQtyClick = (ingredient, type) => {
        setSelectedIngredient(ingredient);
        setModalType(type);
        getIngredientDetails(ingredient.ingredient_id, type);
        setShowModal(true);
    };

    // Handle click on Gross Qty (combined) cell
    const handleGrossQtyClick = (ingredient) => {
        setSelectedIngredient(ingredient);
        setModalType('combined');
        getIngredientDetails(ingredient.ingredient_id, 'combined');
        setShowModal(true);
    };

    // Close modal
    const closeModal = () => {
        setShowModal(false);
        setSelectedIngredient(null);
        setIngredientDetails([]);
        setModalType('');
    };

    const handleSaveStock = async () => {
        setLoading(true);
        try {
            // Save actual stock entries
            const stockEntries = ingredientData.map(item => ({
                ingredient_id: item.ingredient_id,
                date: new Date().toISOString().split('T')[0],
                time_stamp: new Date().toISOString(),
                order_qty: item.order_qty,
                order_pack_nos: item.order_pack_nos,
                quantity: item.actual_qty,
                unit: item.unit
            }));

            const response = await axios.post(`${baseUrl}/stock_ingredient/save`, { entries: stockEntries });
            toast.success("Stock data saved successfully");
        } catch (error) {
            console.error("Error saving stock data:", error);
            toast.error("Failed to save stock data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFromDate('');
        setToDate('');
        setSession([]);
        setItem([]);
        setEvent({});
        setIngredients([]);
        setStore([]);
        setCategory([]);
        setIngredientData([]);
        setSelectedRows([]);
        setSelectAll(false);
        setNoRecordsFound(false);
    };

    console.log("ingredient details:",ingredientDetails);

    return (
        <div className="p-4">
            {loading && <Loader />}
            <Toaster position="top-center" reverseOrder={false} />

            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-2xl font-bold">Raw Material Estimated Order Qty.</h1>
                <div>
                    <Link to="/">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                    </Link>
                    <a
                        href="/actual_stock/add"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                            Edit Actual Qty
                        </button>
                    </a>
                </div>
            </div>

            <div className="relative shadow-md sm:rounded-lg">
                <IngredientFilter
                    handleFilterChange={handleFilterChange}
                    handleExport={handleExport}
                    handleExportPdf={handleExportPdf}
                    handleClear={handleClear}
                    handleClearFilters={() => setFilters({})}
                />

                {noRecordsFound && <NoRecordsFound />}

                {ingredientData.length > 0 && (
                    <>
                        <div className="flex justify-end items-center px-2 mt-4">
                            <button
                                onClick={handleSaveStock}
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                            >
                                Save Stock
                            </button>
                        </div>
                        <div className="overflow-x-auto mt-4">
                            <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                                {/* Gross Required and Net Required were each split Day-wise /
                                    Event-wise. The day-wise half is not shown: what is left is
                                    the event-wise figure, under the plain name of its group.
                                    Both halves are still worked out above -- day-wise is hidden
                                    from the screen and the exports, not dropped from the sums. */}
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 border border-black text-center">
                                            <input
                                                type="checkbox"
                                                onChange={handleSwitchSelectAll}
                                                checked={selectAll}
                                            />
                                        </th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center">Store</th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center">Ingredient Category</th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center">Ingredient Name</th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center">Unit</th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center">Gross Required Qty</th>
                                        {isSuperAdmin && (<>
                                            <th scope="col" className="px-4 py-3 border border-black text-center">Closing Qty (System)</th>
                                            <th scope="col" className="px-4 py-3 border border-black text-center">Available Qty.</th>
                                            <th scope="col" className="px-4 py-3 border border-black text-center">Net Required Qty</th>
                                        </>)}
                                        <th scope="col" className="px-4 py-3 border border-black text-center" colSpan="2">Indent Qty.</th>
                                    </tr>
                                    <tr>
                                        {/* Spacers under the single-row headings above, so Qty and
                                            Pack Nos. sit beneath Indent Qty. */}
                                        <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                        {isSuperAdmin && (<>
                                            <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                            <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                            <th scope="col" className="px-4 py-3 border border-black text-center"></th>
                                        </>)}
                                        <th scope="col" className="px-4 py-3 border border-black text-center">Qty</th>
                                        <th scope="col" className="px-4 py-3 border border-black text-center">Pack Nos.</th>
                                    </tr>
                                    {/* Inline per-column filter row (contains / = / ≤ / ≥ / between / not between) */}
                                    <ColumnFilterRow
                                        thClassName="px-1 py-1 border border-black bg-gray-50 dark:bg-gray-700 align-top"
                                        cols={FILTER_COLS.map((c) => ({ key: c.key, filterable: c.filterable }))}
                                        filters={filters}
                                        onChange={setColFilter} />
                                </thead>
                                <tbody>
                                    {visibleIngredients.map(({ item, index }) => (
                                        <tr key={index} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                            <td className="px-4 py-3 border border-black text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={item.selected}
                                                    onChange={() => handleToggleSelect(index)}
                                                />
                                            </td>
                                            <td className="px-4 py-3 border border-black text-center">{item.store_name}</td>
                                            <td className="px-4 py-3 border border-black text-center">{item.ingredient_category_name}</td>
                                            <td className="px-4 py-3 border border-black text-center">
                                                {item.ingredient_name} {item.eng_name ? `(${item.eng_name})` : ''}
                                            </td>
                                            <td className="px-4 py-3 border border-black text-center">{item.unit}</td>
                                            <td className={`px-4 py-3 border border-black text-center ${item.has_event_wise ? "cursor-pointer hover:bg-blue-100 hover:text-blue-700 underline" : ""}`}
                                                onClick={() => item.has_event_wise && handleQtyClick(item, 'event')}>
                                                {item.event_wise_qty > 0 ? item.event_wise_qty.toFixed(2) : '-'}
                                            </td>
                                            {isSuperAdmin && (<>
                                                <td className="px-4 py-3 border border-black text-center">0</td>
                                                <td className="px-4 py-3 border border-black text-center">
                                                    {item.actual_qty}
                                                </td>
                                                <td className="px-4 py-3 border border-black text-center">
                                                    <span className={item.net_required_event > 0 ? "font-semibold text-green-600" : ""}>
                                                        {item.net_required_event > 0 ? item.net_required_event.toFixed(2) : '-'}
                                                    </span>
                                                </td>
                                            </>)}
                                            {/* <td className="px-4 py-3 border border-black text-center">
                                                <input
                                                    type="number"
                                                    value={item.order_qty}
                                                    onChange={(e) => handleOrderQtyChange(index, e.target.value)}
                                                    className="w-20 px-2 py-1 border rounded-md bg-orange-50"
                                                    step="0.01"
                                                    min="0"
                                                />
                                            </td> */}
                                            <td className="px-4 py-3 border border-black text-center">
                                                <input
                                                    type="number"
                                                    value={item.order_qty !== undefined && item.order_qty !== null ? item.order_qty : ""}
                                                    onChange={(e) => handleOrderQtyChange(index, e.target.value)}
                                                    onBlur={() => handleOrderQtyBlur(index)}
                                                    className="w-20 px-2 py-1 border rounded-md bg-orange-50"
                                                    step="0.01"
                                                    min="0"
                                                />
                                            </td>
                                            <td className="px-4 py-3 border border-black text-center">
                                                <input
                                                    type="number"
                                                    value={item.order_pack_nos || 0}
                                                    onChange={(e) => handleOrderPackNosChange(index, e.target.value)}
                                                    className="w-20 px-2 py-1 border rounded-md bg-orange-50"
                                                    step="0.01"
                                                    min="0"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-lg w-4/5 max-w-4xl max-h-screen overflow-hidden">
                            <div className="p-4 border-b flex justify-between items-center">
                                <h3 className="text-lg font-semibold">
                                    {modalType === 'day' ? 'Day-wise Details - ' :
                                        modalType === 'event' ? 'Event-wise Details - ' :
                                            'Ingredient Details - '}
                                    "{selectedIngredient?.ingredient_name}"
                                    {selectedIngredient?.eng_name ? ` (${selectedIngredient.eng_name})` : ''}
                                </h3>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="p-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
                                {loadingDetails ? (
                                    <div className="flex justify-center items-center p-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : ingredientDetails.length > 0 ? (
                                    <table className="w-full text-sm text-left">
                                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 border">Type</th>
                                                <th className="px-4 py-2 border">Date</th>
                                                <th className="px-4 py-2 border">Session</th>
                                                <th className="px-4 py-2 border">Menu Item</th>
                                                {/* <th className="px-4 py-2 border">Event</th> */}
                                                <th className="px-4 py-2 border">Quantity</th>
                                                {/* <th className="px-4 py-2 border">Unit</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ingredientDetails.map((detail, index) => (
                                                <tr key={index} className="border-b hover:bg-gray-50">
                                                    <td className="px-4 py-2 border">{detail.source_type === 'day' ? 'Day-wise' : 'Event-wise'}</td>
                                                    <td className="px-4 py-2 border">{detail.date_info ? formatDate(detail.date_info) : '-'}</td>
                                                    <td className="px-4 py-2 border">{detail.session_name || '-'}</td>
                                                    {/* Menu Item reads in Gujarati, with the English
                                                        name hidden beside it so either can be searched. */}
                                                    <td className="px-4 py-2 border">
                                                        {detail.menu_item_name
                                                            ? <ItemName item={{ item_name: detail.menu_item_name, eng_name: detail.menu_item_eng_name }} />
                                                            : '-'}
                                                    </td>
                                                    {/* <td className="px-4 py-2 border">{detail.event_name || '-'}</td> */}
                                                    <td className="px-4 py-2 border">{parseFloat(detail.quantity).toFixed(2)}</td>
                                                    {/* <td className="px-4 py-2 border">{detail.unit}</td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        No details found for this ingredient.
                                    </div>
                                )}
                            </div>
                            <div className="p-4 border-t flex justify-end">
                                <button
                                    onClick={closeModal}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring focus:ring-gray-200"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default IngredientRequirementEstimation;